<template>
    <header class="header-nav scrollspy has-mainmenu has-servicemenu">
        <!-- BEGIN main header row -->
        <div class="header-primary">
            <div class="container">
                <div class="row">
                    <div class="col-12 flex-header">
                        <router-link
                            v-if="keycloak"
                            :to="{ name: 'profile' }"
                            class="logo"
                            title="Zur Startseite"
                        >
                            <picture>
                                <source
                                    srcset="
                                        /assets/images/logo/without-claim/CR-Bildmarke-RGB.svg
                                    "
                                    width="26"
                                    height="34"
                                    media="(max-width: 767px)"
                                />

                                <source
                                    srcset="
                                        /assets/images/logo/with-claim/CR-RGB.svg
                                    "
                                />
                                <img
                                    src="/assets/images/logo/with-claim/CR-RGB.svg"
                                    alt="Creditreform"
                                />
                            </picture>
                        </router-link>
                        <a
                            v-else
                            :href="getLogoLink"
                            class="logo"
                            title="Zur Startseite"
                        >
                            <picture>
                                <source
                                    srcset="
                                        /assets/images/logo/without-claim/CR-Bildmarke-RGB.svg
                                    "
                                    width="26"
                                    height="34"
                                    media="(max-width: 767px)"
                                />
                                <source
                                    srcset="
                                        /assets/images/logo/with-claim/CR-RGB.svg
                                    "
                                />
                                <img
                                    src="/assets/images/logo/with-claim/CR-RGB.svg"
                                    alt="Creditreform"
                                />
                            </picture>
                        </a>

                        <LanguageSwitch />

                        <router-link
                            v-if="keycloak"
                            class="login-menu"
                            data-qa="header-primary-logout"
                            :to="{ name: 'logout' }"
                        >
                            <span>{{ t('Navigation.Logout') }}</span>
                            <!-- eslint-disable-next-line vue/max-attributes-per-line -->
                            <i
                                class="crefo-ui-icon icon-enter icon-color-blue"
                                aria-hidden="true"
                            />
                        </router-link>
                        <a
                            v-if="keycloak"
                            href="javascript:void(0)"
                            class="mobile-main-menu-opener"
                            @click="toggleMobileNavigation"
                        >
                            <i
                                data-qa="icon-mobile"
                                class="crefo-ui-icon icon-color-blue"
                                :class="{
                                    'icon-mobile-menu': !mobileOpen,
                                    'icon-close': mobileOpen
                                }"
                                aria-hidden="true"
                            />
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <!-- END main header row -->
        <!-- BEGIN secondary header row -->
        <div v-if="keycloak" class="header-secondary">
            <div class="container">
                <div class="row">
                    <div class="col-12">
                        <ul class="main-menu">
                            <li class="menu-item show-if-sticky">
                                <a href="/" title="Zur Startseite">
                                    <img
                                        src="/assets/images/logo/without-claim/CR-Bildmarke-RGB.svg"
                                        width="26"
                                        height="34"
                                        alt="Creditreform"
                                    />
                                </a>
                            </li>
                            <li class="menu-item">
                                <router-link
                                    :to="{ name: 'profile' }"
                                    data-qa="navigationPersonalDataLink"
                                >
                                    {{ t('Navigation.Profile') }}
                                </router-link>
                            </li>
                            <li v-if="showSecurity" class="menu-item">
                                <router-link
                                    :to="{ name: 'security' }"
                                    data-qa="navigationSecurityLink"
                                >
                                    {{ t('Navigation.Security') }}
                                </router-link>
                            </li>
                            <li v-if="showCompanyData" class="menu-item">
                                <router-link
                                    :to="{ name: 'company' }"
                                    data-qa="navigationCompanyDataLink"
                                >
                                    {{ t('Navigation.Company') }}
                                </router-link>
                            </li>
                            <li class="menu-item">
                                <router-link
                                    :to="{ name: 'membership' }"
                                    data-qa="navigationMembershipLink"
                                >
                                    {{ t('Navigation.Membership') }}
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
        <!-- END secondary header row -->
        <nav v-if="keycloak" class="mobile-nav-wrapper">
            <ul @click="closeMobileNavigation">
                <li class="mobile-menu-item">
                    <router-link
                        :to="{ name: 'profile' }"
                        data-qa="navigationPersonalDataLink"
                    >
                        {{ t('Navigation.Profile') }}
                    </router-link>
                </li>
                <li v-if="showSecurity" class="mobile-menu-item">
                    <router-link
                        :to="{ name: 'security' }"
                        data-qa="navigationSecurityLink"
                    >
                        {{ t('Navigation.Security') }}
                    </router-link>
                </li>
                <li v-if="showCompanyData" class="mobile-menu-item">
                    <router-link
                        :to="{ name: 'company' }"
                        data-qa="navigationCompanyDataLink"
                    >
                        {{ t('Navigation.Company') }}
                    </router-link>
                </li>
                <li class="mobile-menu-item">
                    <router-link
                        :to="{ name: 'membership' }"
                        data-qa="navigationMembershipLink"
                    >
                        {{ t('Navigation.Membership') }}
                    </router-link>
                </li>
                <li class="service-menu-item mobile-menu-item">
                    <router-link :to="{ name: 'logout' }">
                        {{ t('Navigation.Logout') }}
                    </router-link>
                </li>
            </ul>
        </nav>
    </header>
</template>
<script setup>
import LanguageSwitch from '@/components/navigation/partials/LanguageSwitch'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/store/module-user.js'
import { storeToRefs } from 'pinia'

const { t } = useI18n()
const mobileOpen = ref(false)
const store = useUserStore()
const {
    getLogoLink,
    keycloak,
    showCompanyData,
    isMitgliedsUser,
    getUser: user
} = storeToRefs(store)

const feature2FAActive = `${import.meta.env.VITE_FEATURE_2FA_ACTIVE}`
const feature2FARestrictedToClzs = `${import.meta.env.VITE_FEATURE_2FA_RESTRICTED_TO_CLZS}`

const showSecurity = computed(() => {
    // wenn der globale Switch aktiv ist
    if (feature2FAActive === 'true') {
        // nachschauen, ob explizite CLZs gesetzt sind, für die das Feature aktiv sein soll
        // sind keine CLZ-Restriktionen gesetzt
        if (feature2FARestrictedToClzs.trim().length === 0) {
            // Feature für alles und jeden aktiv
            return true
        } else {
            // ansonsten gelten CLZ-Restriktionen
            // wenn es ein Mitgliedsbenutzer ist
            if (isMitgliedsUser.value) {
                // lohnt es sich zu prüfen, ob CLZ des angemeldeten Benutzers in denen enthalten ist, für die 2FA aktiv sein soll
                let userClz =
                        user.value.authenticated?.tokenParsed?.cr_membernumber
                            .toString()
                            .substring(0, 3)
                return feature2FARestrictedToClzs.includes(userClz)
            }
        }
    }
    // ansonsten ausblenden
    return false
})

const toggleMobileNavigation = () => {
    mobileOpen.value = !mobileOpen.value
    document.body.classList.toggle('mobile-menu-opened')
}

const closeMobileNavigation = () => {
    mobileOpen.value = false
    document.body.classList.toggle('mobile-menu-opened')
}
</script>

<style scoped lang="less">
    .header-nav a.logo {
        left: 15px;
    }
    .flex-header {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: nowrap;

        .login-menu {
            border-left: 1px solid #edece5;
            border-right: 1px solid #edece5;
            padding: 0 13px;
            align-self: stretch;
            display: none;
            align-items: center;
            position: relative;

            span {
                display: inline-block;
                vertical-align: top;
                line-height: 23px;
                margin-right: 10px;
            }

            i::before {
                transition: color 0.15s ease-in-out;
            }

            &:hover {
                i::before {
                    color: #004884 !important;
                }
            }
        }
    }
    .mobile-menu-item.service-menu-item {
        display: block;
    }
    @media only screen and (min-width: 1200px) {
        .login-menu {
            display: flex !important;
        }
    }
    @media only screen and (min-width: 1200px) {
        .mobile-menu-item.service-menu-item {
            display: none;
        }
    }
</style>
